import { buildSnowplowPublicContext } from './common-properties';
import { shouldTrackSnowplowError } from './error-filter';
import { canUseSnowplowGzipFetch, createSnowplowGzipFetch } from './gzip-fetch';
import { getSnowplowPixels } from './pixel-mapping';
import { SNOWPLOW_EVENT_SCHEMA } from './types';

import type {
  SnowplowDebugState,
  SnowplowEventParams,
  SnowplowQueuedEvent,
  SnowplowResolvedConfig,
  SnowplowSelfDescribingJson,
} from './types';
import type { BrowserPlugin } from '@snowplow/browser-tracker';

type BrowserTrackerModule = typeof import('@snowplow/browser-tracker');
type ErrorTrackingModule = typeof import('@snowplow/browser-plugin-error-tracking');

type SnowplowLocalUserInfo = {
  id?: number | string;
  email?: string;
  telephone?: string;
};

type SnowplowUserData = {
  customer_id?: number | string;
  email?: string;
  phone?: string;
};

const SNOWPLOW_TRACKER_NAMESPACE = 'sp1';
const MAX_QUEUE_SIZE = 50;

let trackerModule: BrowserTrackerModule | null = null;
let resolvedConfig: SnowplowResolvedConfig | null = null;
let hasResolvedConfig = false;
let initialized = false;
let initPromise: Promise<boolean> | null = null;
// 记录最近一次接收的 PageView URL，避免同一落地页或同一次路由状态被重复放入 Snowplow 队列。
let lastPageViewDedupeKey = '';
const queue: SnowplowQueuedEvent[] = [];

/**
 * 获取当前 provider 调试状态。
 * @returns Snowplow debug 状态
 */
export function getSnowplowDebugState(): SnowplowDebugState {
  return {
    enabled: !!resolvedConfig?.enabled,
    initialized,
    queueLength: queue.length,
    collectorUrl: resolvedConfig?.collectorUrl || '',
    postPath: resolvedConfig?.postPath || '',
    gzipEnabled: !!resolvedConfig?.gzipEnabled,
    errorTrackingEnabled: !!resolvedConfig?.errorTrackingEnabled,
    disabledReason: resolvedConfig?.disabledReason || '',
  };
}

/**
 * 暂存初始化前触发的事件。
 * @param eventName - 神策事件名
 * @param params - 过滤后的事件参数
 */
function enqueueEvent(eventName: string, params: SnowplowEventParams): void {
  queue.push({ eventName, params });
  if (queue.length > MAX_QUEUE_SIZE) {
    queue.shift();
  }
}

/**
 * 获取 PageView 去重 key，优先使用神策统一入口补充的完整 URL。
 * @param params - 过滤后的事件参数
 * @returns PageView 去重 key
 */
function getPageViewDedupeKey(params: SnowplowEventParams): string {
  if (typeof params.$url === 'string' && params.$url) {
    return params.$url;
  }
  return process.client ? window.location.href : '';
}

/**
 * 判断当前 PageView 是否与最近一次已发送 URL 重复。
 * @param pageViewKey - PageView URL 去重 key
 * @returns true 表示当前 PageView 需要跳过
 */
function isDuplicateSentPageView(pageViewKey: string): boolean {
  return !!pageViewKey && pageViewKey === lastPageViewDedupeKey;
}

/**
 * 记录最近一次已发送的 PageView URL。
 * @param pageViewKey - PageView URL 去重 key
 */
function markPageViewSent(pageViewKey: string): void {
  if (pageViewKey) {
    lastPageViewDedupeKey = pageViewKey;
  }
}

/**
 * 安全读取本地用户信息，避免 localStorage 或 JSON 异常影响埋点链路。
 * @returns 本地用户信息
 */
function getSnowplowLocalUserInfo(): SnowplowLocalUserInfo {
  try {
    const raw = localStorage.getItem('voo-userInfo');
    const parsed = raw ? (JSON.parse(raw) as unknown) : null;
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as SnowplowLocalUserInfo)
      : {};
  } catch {
    return {};
  }
}

/**
 * 构建 Snowplow 用户数据对象，用于观鹿侧用户维度对账。
 * @returns Snowplow user_data 字段
 */
function buildSnowplowUserData(): SnowplowUserData {
  const info = getSnowplowLocalUserInfo();
  return {
    customer_id: info.id,
    email: info.email,
    phone: info.telephone,
  };
}

/**
 * 将 Snowplow 事件 ID 转成字符串。
 * @param value 事件 ID 原始值
 * @returns 字符串事件 ID
 */
function stringifySnowplowEventId(value: unknown): string {
  return typeof value === 'string' || typeof value === 'number' ? `${value}` : '';
}

/**
 * 读取 Snowplow 使用的 sensors_event_id，兼容旧 event_id 调用。
 * @param params - 事件参数
 * @returns Snowplow 事件 ID
 */
function getSnowplowSensorsEventId(params: SnowplowEventParams): string {
  return (
    stringifySnowplowEventId(params.sensors_event_id) || stringifySnowplowEventId(params.event_id)
  );
}

/**
 * 规范化 Snowplow 事件参数，避免 Snowplow 继续上报 event_id。
 * @param params - 事件参数
 * @returns 规范化后的事件参数
 */
function normalizeSnowplowEventParams(params: SnowplowEventParams): SnowplowEventParams {
  const normalizedParams = { ...params };
  const sensorsEventId = getSnowplowSensorsEventId(params);
  delete normalizedParams.event_id;
  if (sensorsEventId) {
    normalizedParams.sensors_event_id = sensorsEventId;
  }
  return normalizedParams;
}

/**
 * 构建事件 context。
 * @param params - 事件参数
 * @returns Snowplow context 数组
 */
function buildContext(params: SnowplowEventParams): SnowplowSelfDescribingJson[] {
  if (!resolvedConfig) return [];
  return [
    buildSnowplowPublicContext(resolvedConfig),
    {
      schema: SNOWPLOW_EVENT_SCHEMA,
      data: {
        sensors_event_id: getSnowplowSensorsEventId(params),
        user_data: buildSnowplowUserData(),
      },
    },
  ];
}

/**
 * 发送 PageView。
 * @param params - PageView 参数
 */
function sendPageView(params: SnowplowEventParams): void {
  trackerModule?.trackPageView(
    {
      context: buildContext(params),
    },
    [SNOWPLOW_TRACKER_NAMESPACE],
  );
}

/**
 * 发送自描述事件。
 * @param eventName - 事件名称
 * @param params - 事件参数
 */
function sendSelfDescribingEvent(eventName: string, params: SnowplowEventParams): void {
  trackerModule?.trackSelfDescribingEvent(
    {
      event: {
        schema: SNOWPLOW_EVENT_SCHEMA,
        data: {
          event_name: eventName,
          properties: params,
          pixel: getSnowplowPixels(eventName),
        },
      },
      context: buildContext(params),
    },
    [SNOWPLOW_TRACKER_NAMESPACE],
  );
}

/**
 * 发送队列中缓存的事件。
 */
function flushQueue(): void {
  const pending = queue.splice(0, queue.length);
  pending.forEach(({ eventName, params }) => {
    trackSnowplowProviderEvent(eventName, params);
  });
}

/**
 * 初始化 Snowplow 自动错误采集。
 * @param errorModule - Snowplow error tracking plugin module
 */
function enableSnowplowErrorTracking(errorModule: ErrorTrackingModule): void {
  errorModule.enableErrorTracking(
    {
      filter: shouldTrackSnowplowError,
    },
    [SNOWPLOW_TRACKER_NAMESPACE],
  );
}

/**
 * 初始化 Snowplow browser tracker。
 * @param config - 已解析的 Snowplow 配置
 * @returns true 表示初始化成功
 */
export function initSnowplowProvider(config: SnowplowResolvedConfig): Promise<boolean> {
  resolvedConfig = config;
  hasResolvedConfig = true;
  queue.length = config.enabled ? queue.length : 0;

  if (!config.enabled) return Promise.resolve(false);
  if (initialized) return Promise.resolve(true);
  if (initPromise) return initPromise;

  initPromise = (async () => {
    try {
      const tracker = await import('@snowplow/browser-tracker');
      trackerModule = tracker;

      let plugins: BrowserPlugin[] = [];
      let errorModule: ErrorTrackingModule | null = null;
      if (config.errorTrackingEnabled) {
        errorModule = await import('@snowplow/browser-plugin-error-tracking');
        plugins = [errorModule.ErrorTrackingPlugin()];
      }

      tracker.newTracker(SNOWPLOW_TRACKER_NAMESPACE, config.collectorUrl, {
        appId: config.appId,
        platform: 'web',
        eventMethod: 'post',
        postPath: config.postPath,
        contexts: {
          webPage: true,
        },
        preservePageViewIdForUrl: 'pathnameAndSearch',
        plugins,
        customFetch:
          config.gzipEnabled && canUseSnowplowGzipFetch() ? createSnowplowGzipFetch() : undefined,
      });

      if (errorModule) {
        enableSnowplowErrorTracking(errorModule);
      }

      initialized = true;
      flushQueue();
      return true;
    } catch (error) {
      console.error('@snowplow--init', error);
      initialized = false;
      trackerModule = null;
      if (resolvedConfig) {
        resolvedConfig = {
          ...resolvedConfig,
          enabled: false,
          disabledReason: 'init_failed',
        };
      }
      queue.length = 0;
      return false;
    } finally {
      initPromise = null;
    }
  })();

  return initPromise;
}

/**
 * 通过 Snowplow provider 发送事件。
 * @param eventName - 神策事件名
 * @param params - 过滤后的事件参数
 */
export function trackSnowplowProviderEvent(
  eventName: string,
  params: SnowplowEventParams = {},
): void {
  try {
    if (!process.client) return;
    const normalizedParams = normalizeSnowplowEventParams(params);
    if (!hasResolvedConfig) {
      enqueueEvent(eventName, normalizedParams);
      return;
    }
    if (!resolvedConfig?.enabled) return;
    if (!initialized || !trackerModule) {
      enqueueEvent(eventName, normalizedParams);
      return;
    }
    if (eventName === 'PageView') {
      const pageViewKey = getPageViewDedupeKey(normalizedParams);
      if (isDuplicateSentPageView(pageViewKey)) return;
      sendPageView(normalizedParams);
      markPageViewSent(pageViewKey);
      return;
    }
    sendSelfDescribingEvent(eventName, normalizedParams);
  } catch (error) {
    console.error('@snowplow--track', error);
  }
}

/**
 * 判断 PageLeave 是否允许启用。
 * @returns true 表示可以启用 PageLeave
 */
export function canTrackSnowplowPageLeave(): boolean {
  return !!resolvedConfig?.enabled && initialized;
}
